import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

// Define the default parameters
const DEFAULT_PARAMS = {
  onResize: () => {},
};

// Define the type for the resize handler
export type ResizeHandler = (width?: number) => void;

// Define the type for the context value
interface ResizeContextValue {
  windowWidth: number;
}

// Create the context with a default value
const ResizeContext = createContext<ResizeContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useResize({
  onResize,
}: { onResize: ResizeHandler } = DEFAULT_PARAMS) {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    onResize(window.innerWidth);

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      onResize(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [onResize]);

  return windowWidth;
}

// Create a provider component
interface ResizeProviderProps {
  children: ReactNode;
}

export const ResizeProvider: React.FC<ResizeProviderProps> = ({ children }) => {
  const windowWidth = useResize();

  return (
    <ResizeContext.Provider value={{ windowWidth }}>
      {children}
    </ResizeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useWindowWidth = (): number => {
  const context = useContext(ResizeContext);
  if (!context) {
    throw new Error("useWindowWidth must be used within a ResizeProvider");
  }
  return context.windowWidth;
};
